import { useState } from 'react';
import axios from 'axios';
import { PaymentElement, LinkAuthenticationElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, AlertCircle } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * EmbeddedPaymentForm — inline Stripe Payment Element.
 * Confirms payment on-page (no redirect for card payments).
 * For redirect-based methods (Klarna, Affirm), Stripe handles the redirect
 * to `returnUrl` automatically and brings the user back to /checkout/success.
 *
 * When `finalizeSubscription` is true, after the PaymentIntent succeeds we
 * call /api/glow-up-gap/finalize-trial which creates the recurring $37/mo
 * subscription (with or without trial, depending on PI metadata).
 * Used by both the Glow Up Gap $9 trial flow AND the new $37/mo direct flow.
 */
export default function EmbeddedPaymentForm({ returnUrl, ctaLabel = 'Complete Purchase', disabled = false, defaultEmail = '', finalizeSubscription = false }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(defaultEmail);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setSubmitting(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: email ? { billing_details: { email } } : undefined,
        receipt_email: email || undefined,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      // Show error from Stripe (declined card, validation, etc.)
      setError(confirmError.message || 'Payment failed. Please try again.');
      setSubmitting(false);
      return;
    }

    // Card payment succeeded without redirect — finalize subscription if applicable, then navigate.
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      if (finalizeSubscription) {
        try {
          await axios.post(`${API}/glow-up-gap/finalize-trial`, {
            payment_intent_id: paymentIntent.id,
          });
        } catch (finalizeErr) {
          // Payment already succeeded — surface a soft error but still navigate so user reaches success page.
          // The webhook fallback / admin tools can recover the subscription if it didn't get created.
          console.error('Subscription finalize failed:', finalizeErr);
        }
      }
      const successUrl = new URL(returnUrl);
      successUrl.searchParams.set('payment_intent', paymentIntent.id);
      successUrl.searchParams.set('redirect_status', 'succeeded');
      window.location.href = successUrl.toString();
      return;
    }

    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="embedded-payment-form">
      <div className="mb-3">
        <LinkAuthenticationElement
          options={{ defaultValues: { email: defaultEmail } }}
          onChange={(e) => setEmail(e.value?.email || '')}
        />
      </div>
      <div className="mb-4">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2" data-testid="payment-error">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || submitting || disabled}
        className="w-full btn-gold py-5 text-sm sm:text-base whitespace-nowrap"
        data-testid="embedded-pay-button"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2 shrink-0" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4 mr-2 shrink-0" />
            <span className="truncate">{ctaLabel}</span>
          </>
        )}
      </Button>
    </form>
  );
}
